import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setShowObjectiveCompleteNotification } from '../../common/state/mission/reducer'
import {
  NOTIFICATION_ANIMATION_MS,
  OBJECTIVE_COMPLETE_NOTIFICATION_DURATION_MS,
} from './missionCompleteConstants'
import { MissionStarBurst } from './MissionStarBurst'

const OBJECTIVE_COMPLETED_IMAGE = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/story-mission/objective-completed-notification.webp'

export const MissionObjectiveCompleteNotification: React.FC = () => {
  const show = useSelector(
    (state: RootState) => state.app.mission.showObjectiveCompleteNotification
  )
  const description = useSelector(
    (state: RootState) => state.app.mission.objectiveCompleteDescription ?? ''
  )
  const dispatch = useDispatch()
  const [exiting, setExiting] = React.useState(false)
  const [entered, setEntered] = React.useState(false)
  const [burstKey, setBurstKey] = React.useState(0)
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  React.useEffect(() => {
    if (!show) {
      setEntered(false)
      setExiting(false)
      return
    }
    setEntered(false)
    const enterId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEntered(true)
        setBurstKey((k) => k + 1)
      })
    })
    hideTimerRef.current = setTimeout(() => {
      setExiting(true)
    }, OBJECTIVE_COMPLETE_NOTIFICATION_DURATION_MS)
    return () => {
      cancelAnimationFrame(enterId)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [show])

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName !== 'transform') return
      if (exiting) {
        dispatch(setShowObjectiveCompleteNotification(false))
        setExiting(false)
      }
    },
    [exiting, dispatch]
  )

  if (!show && !exiting) return null
  const isOffScreen = !entered || exiting
  return (
    <div
      className="fixed left-1/2 top-0 z-[200] flex -translate-x-1/2 flex-col items-center pointer-events-none"
      style={{ willChange: 'transform' }}
      aria-live="polite"
      role="status"
    >
      <div
        onTransitionEnd={handleTransitionEnd}
        className="flex flex-col items-center transition-transform duration-300 ease-out"
        style={{
          transform: isOffScreen ? 'translateY(-100%)' : 'translateY(0)',
          transitionDuration: `${NOTIFICATION_ANIMATION_MS}ms`,
        }}
      >
        <div className="relative w-max max-w-[min(92vw,560px)] min-w-0">
          <div className="relative z-10">
            <div
              className="flex w-max min-w-0 max-w-full flex-col items-center justify-center gap-1 px-6 py-4 text-center drop-shadow-lg sm:px-10 sm:py-5"
              style={{
                backgroundImage: `url(${OBJECTIVE_COMPLETED_IMAGE})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >
              <span
                className="max-w-full break-words text-lg font-bold text-amber-900"
                style={{ textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}
              >
                Objective Completed!
              </span>
              {description && (
                <span
                  className="max-w-full break-words px-0.5 text-sm font-medium text-amber-800"
                  style={{ textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}
                >
                  {description}
                </span>
              )}
            </div>
          </div>
          <MissionStarBurst burstKey={burstKey} visible={entered && !exiting} />
        </div>
      </div>
    </div>
  )
}
